import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { Building2, Plus } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { ListView } from "../../../ui/components/ListView";
import type { CardRenderContext } from "../../../ui/components/ListView";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth, usePermission } from "../../lib/auth/hooks";
import * as userApi from "../../lib/api/users";
import { buildOrgSelectionActions } from "./orgsBulkActions";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import { useWebSocket } from "../../hooks/useWebSocket";
import type { ListParams } from "../../../ui/api/listApi.types";
import { ORG_TABLE_COLUMNS, type OrgListItem } from "./organizationsTableColumns";
import { ListViewOrganizationCard } from "../../../ui/components/ListView/components/ListViewOrganizationCard";
import { CreateOrganizationModal } from "../profile/CreateOrganizationModal";
import { useListImportExport } from "../data-transfer/useListImportExport";
import {
  orgContextHash,
  useOrganizationContextOptional,
} from "../../lib/organization-context";
import { useI18n } from "../../lib/i18n";
import * as pageStyles from "../users/UsersListPage.css";

export function OrganizationsListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const { accessToken, user, refreshUser } = useAuth();
  const { hasPermissionSync: canDisable } = usePermission("organization:update");
  const { hasPermissionSync: canHardDelete } = usePermission("organization:delete");
  const orgContext = useOrganizationContextOptional();
  const selectedOrgIds = orgContext?.selectedOrgIds ?? [];
  const contextKey = orgContext ? orgContextHash(selectedOrgIds) : "none";
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [pendingSelectOrgId, setPendingSelectOrgId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!pendingSelectOrgId || !orgContext) return;
    if (!orgContext.availableOrgs.some((o) => o.id === pendingSelectOrgId)) return;
    orgContext.setSelectedOrgIds([...orgContext.selectedOrgIds, pendingSelectOrgId]);
    setPendingSelectOrgId(null);
  }, [pendingSelectOrgId, orgContext]);

  const token =
    accessToken ??
    (typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null);

  const refresh = React.useCallback(() => setRefreshKey((k) => k + 1), []);
  const { subscribe, unsubscribe } = useWebSocket();
  const { confirm, ConfirmModal } = useConfirmModal();

  React.useEffect(() => {
    subscribe(['entity:organizations:*'], refresh);
    return () => unsubscribe(['entity:organizations:*'], refresh);
  }, [subscribe, unsubscribe, refresh]);

  // After a bulk mutation, refresh the table AND the user's org list so the
  // NavigationBar organization context control removes the deleted orgs immediately.
  const onMutationComplete = React.useCallback(() => {
    refresh();
    void refreshUser?.();
  }, [refresh, refreshUser]);

  const [listTotal, setListTotal] = React.useState(0);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [exportPreviewItems, setExportPreviewItems] = React.useState<
    Array<{ id: string; label: string; matchKey?: string }>
  >([]);

  const selectionActions = React.useMemo(
    () =>
      token
        ? buildOrgSelectionActions({
            accessToken: token,
            canDisable: canDisable(),
            canHardDelete: canHardDelete(),
            confirm,
            onMutationComplete,
          })
        : [],
    [token, canDisable, canHardDelete, confirm, onMutationComplete],
  );

  const {
    headerActions: importExportHeaderActions,
    exportContextAction,
    modals: importExportModals,
  } = useListImportExport({
    entity: "organizations",
    entityLabel: "Organizations",
    enableImport: true,
    enableExport: true,
    totalCount: listTotal,
    selectedIds,
    previewItems: exportPreviewItems,
    defaultFormat: "json",
    onImported: onMutationComplete,
  });

  const selectionActionsWithExport = React.useMemo(() => {
    if (!exportContextAction) return selectionActions;
    return [
      ...selectionActions,
      {
        icon: exportContextAction.icon,
        label: exportContextAction.label,
        onClick: (ids: string[]) => exportContextAction.onClick(ids),
      },
    ];
  }, [selectionActions, exportContextAction]);

  const fetchOrganizations = React.useCallback(
    async (params: ListParams) => {
      if (!token) {
        return { data: [] as OrgListItem[], total: 0 };
      }
      const orgs = await userApi.fetchOrganizations(token);
      let items = orgs as OrgListItem[];

      if (
        orgContext?.hasOrganizationMembership &&
        selectedOrgIds.length > 0
      ) {
        const allowed = new Set(selectedOrgIds);
        items = items.filter((org) => allowed.has(org.id));
      }

      if (params.search?.trim()) {
        const q = params.search.toLowerCase();
        items = items.filter(
          (o) =>
            o.name.toLowerCase().includes(q) ||
            o.slug?.toLowerCase().includes(q) ||
            o.description?.toLowerCase().includes(q) ||
            o.city?.toLowerCase().includes(q) ||
            o.state?.toLowerCase().includes(q) ||
            o.email?.toLowerCase().includes(q),
        );
      }

      const statusFilter = params.filters?.status;
      if (Array.isArray(statusFilter) && statusFilter.length > 0) {
        const allowed = new Set(statusFilter.map(String));
        items = items.filter((org) =>
          allowed.has(org.deletedAt ? "disabled" : "active"),
        );
      }

      const stateFilter = params.filters?.state;
      if (Array.isArray(stateFilter) && stateFilter.length > 0) {
        const allowed = new Set(stateFilter.map(String));
        items = items.filter((org) => org.state && allowed.has(org.state));
      }

      if (params.sort_by) {
        const key = params.sort_by as keyof OrgListItem;
        const dir = params.order === "desc" ? -1 : 1;
        items = [...items].sort((a, b) => {
          const av = (a[key] ?? "") as string;
          const bv = (b[key] ?? "") as string;
          return av.localeCompare(bv) * dir;
        });
      }

      const total = items.length;
      const start = ((params.page ?? 1) - 1) * (params.limit ?? 25);
      const paged = items.slice(start, start + (params.limit ?? 25));

      setExportPreviewItems(
        items.map((o) => ({
          id: o.id,
          label: o.name,
          matchKey: o.slug ?? o.id,
        })),
      );
      setListTotal(total);
      return { data: paged, total };
    },
    [token, orgContext?.hasOrganizationMembership, selectedOrgIds.join(",")],
  );

  const showContextHint =
    orgContext != null &&
    orgContext.hasOrganizationMembership &&
    orgContext.availableOrgs.length > orgContext.selectedOrgIds.length;

  const tableFooter = showContextHint ? (
    <div className={pageStyles.contextHintBanner} role="status">
      <Building2
        className={pageStyles.contextHintIcon}
        strokeWidth={1.75}
        size={14}
        aria-hidden
      />
      <span>
        Some of your organizations are hidden by the current context. Change
        context in the navigation bar to include them.
      </span>
    </div>
  ) : undefined;

  if (!token) {
    return (
      <div className={pageStyles.page}>
        <Callout variant="error">{t("pro.orgs.signInRequired")}</Callout>
      </div>
    );
  }

  return (
    <div className={pageStyles.page}>
      <div className={pageStyles.pageHeader}>
        <Text level="h1" color="text" weight="bold">
          Organizations
        </Text>
      </div>

      <div className={pageStyles.tableHost}>
        <ListView<OrgListItem>
          listId="organizations"
          userId={user?.id ?? "anonymous"}
          columns={ORG_TABLE_COLUMNS}
          fetchList={fetchOrganizations}
          contextKey={`${contextKey}:${refreshKey}`}
          footer={tableFooter}
          fillContainer
          allowFullscreen
          enableSavedViews
          resourceType="organizations"
          enabledViews={["table", "deck", "stack"]}
          enableSelection={Boolean(selectionActionsWithExport.length)}
          selectionActions={selectionActionsWithExport}
          onSelectionChange={setSelectedIds}
          headerActions={[
            ...importExportHeaderActions,
            {
              id: "create",
              label: "Create organization",
              icon: <Plus size={14} />,
              color: "primary",
              onClick: () => setShowCreateModal(true),
            },
          ]}
          getRowLabel={row => row.name ?? row.id}
          searchPlaceholder="Search organizations by name, location, or email…"
          emptyTitle="No organizations found"
          emptySubtitle={t("pro.orgs.emptySubtitle")}
          emptyIcon="users"
          defaultSort={{ sort_by: "name", order: "asc" }}
          onRowClick={(row) =>
            navigate({ to: "/workspace/organizations/$orgId", params: { orgId: row.id } })
          }
          renderCard={(row: OrgListItem, ctx: CardRenderContext) => (
            <ListViewOrganizationCard
              key={row.id}
              layout="deck"
              organization={row}
              onClick={ctx.onClick}
            />
          )}
          renderStackCard={(row: OrgListItem, ctx: CardRenderContext) => (
            <ListViewOrganizationCard
              key={row.id}
              layout="stack"
              organization={row}
              onClick={ctx.onClick}
            />
          )}
        />
      </div>

      <CreateOrganizationModal
        open={showCreateModal}
        setOpen={setShowCreateModal}
        onCreated={(orgId) => {
          setShowCreateModal(false);
          void refreshUser?.();
          void queryClient.invalidateQueries({ queryKey: ["organizations", "list"] });
          setPendingSelectOrgId(orgId);
        }}
      />
      <ConfirmModal />
      {importExportModals}
    </div>
  );
}
